import { useMutation, useQuery } from '@apollo/client/react'
import { Loader, Upload } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { GQL_APP_CONFIG, GQL_UPDATE_APP_CONFIG } from '~/gql/app-config/app-config'
import PlanLockedBanner from '~/pages/backoffice/admin/billing/PlanLockedBanner'

/** Taille max d'un asset uploadé en data-URI (base64 stocké en DB). */
const MAX_ASSET_BYTES = 512 * 1024

type FormState = {
  appName: string
  primaryColor: string
  accentColor: string
  headingFont: string
  bodyFont: string
  footerText: string
  defaultLocale: string
  logo: string
  favicon: string
}

const EMPTY_FORM: FormState = {
  appName: '',
  primaryColor: '',
  accentColor: '',
  headingFont: '',
  bodyFont: '',
  footerText: '',
  defaultLocale: 'fr',
  logo: '',
  favicon: '',
}

/** Lit un fichier image en data-URI base64. */
function readFileAsDataUri(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

function BrandingSettings() {
  const { t } = useTranslation()
  const { data, loading, refetch } = useQuery(GQL_APP_CONFIG)
  const [updateAppConfig, { loading: saving }] = useMutation(GQL_UPDATE_APP_CONFIG)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)

  // Plan gratuit : white-label verrouillé (#155). Grisage côté écran ; le refus
  // reste garanti par l'API, ce drapeau vient de la même source (le palier).
  const locked = Boolean(data?.appConfig?.brandingLocked)

  useEffect(() => {
    const cfg = data?.appConfig
    if (!cfg) return
    setForm({
      appName: cfg.appName ?? '',
      primaryColor: cfg.primaryColor ?? '',
      accentColor: cfg.accentColor ?? '',
      headingFont: cfg.headingFont ?? '',
      bodyFont: cfg.bodyFont ?? '',
      footerText: cfg.footerText ?? '',
      defaultLocale: cfg.defaultLocale ?? 'fr',
      logo: cfg.logo ?? '',
      favicon: cfg.favicon ?? '',
    })
  }, [data])

  const set = (key: keyof FormState) => (value: string) =>
    setForm(prev => ({ ...prev, [key]: value }))

  const handleFile = (key: 'logo' | 'favicon') => async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > MAX_ASSET_BYTES) {
      toast.error(t('branding.imageTooLarge'))
      return
    }
    try {
      const dataUri = await readFileAsDataUri(file)
      set(key)(dataUri)
    } catch {
      toast.error(t('branding.readError'))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (locked) return
    try {
      await updateAppConfig({
        variables: {
          input: {
            appName: form.appName || undefined,
            primaryColor: form.primaryColor || undefined,
            accentColor: form.accentColor || undefined,
            headingFont: form.headingFont || undefined,
            bodyFont: form.bodyFont || undefined,
            footerText: form.footerText || undefined,
            defaultLocale: form.defaultLocale || undefined,
            logo: form.logo || undefined,
            favicon: form.favicon || undefined,
          },
        },
      })
      await refetch()
      toast.success(t('branding.saved'))
    } catch {
      toast.error(t('branding.saveError'))
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center p-10">
        <Loader className="size-6 animate-spin" />
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="mx-auto flex w-full max-w-2xl flex-col gap-6 p-4">
      {locked && <PlanLockedBanner message={t('branding.locked')} />}
      {/* fieldset[disabled] grise en bloc tous les champs, boutons et uploads —
          `contents` préserve l'espacement flex du formulaire. */}
      <fieldset disabled={locked} className="contents">
        <Card>
          <CardHeader>
            <CardTitle>{t('branding.identity')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="appName">{t('branding.appName')}</Label>
              <Input
                id="appName"
                value={form.appName}
                onChange={e => set('appName')(e.target.value)}
                placeholder="LMS"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>{t('branding.logo')}</Label>
              <div className="flex items-center gap-4">
                {form.logo
                  ? <img src={form.logo} alt="logo" className="h-12 w-auto rounded border border-border object-contain" />
                  : <span className="text-sm text-muted-foreground">{t('branding.noLogo')}</span>}
                <Button type="button" variant="outline" size="sm" asChild>
                  <label className="cursor-pointer">
                    <Upload className="mr-2 size-4" />
                    {t('common.choose')}
                    <input type="file" accept="image/*" className="hidden" onChange={handleFile('logo')} />
                  </label>
                </Button>
                {form.logo && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => set('logo')('')}>
                    {t('common.remove')}
                  </Button>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>{t('branding.favicon')}</Label>
              <div className="flex items-center gap-4">
                {form.favicon
                  ? <img src={form.favicon} alt="favicon" className="size-8 rounded border border-border object-contain" />
                  : <span className="text-sm text-muted-foreground">{t('branding.noFavicon')}</span>}
                <Button type="button" variant="outline" size="sm" asChild>
                  <label className="cursor-pointer">
                    <Upload className="mr-2 size-4" />
                    {t('common.choose')}
                    <input type="file" accept="image/*" className="hidden" onChange={handleFile('favicon')} />
                  </label>
                </Button>
                {form.favicon && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => set('favicon')('')}>
                    {t('common.remove')}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('branding.colorsTypography')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="primaryColor">{t('branding.primaryColor')}</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={form.primaryColor || '#000000'}
                    onChange={e => set('primaryColor')(e.target.value)}
                    className="size-9 cursor-pointer rounded border border-border bg-transparent"
                    aria-label={t('branding.primaryColor')}
                  />
                  <Input
                    value={form.primaryColor}
                    onChange={e => set('primaryColor')(e.target.value)}
                    placeholder="#000000"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="accentColor">{t('branding.accentColor')}</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={form.accentColor || '#F1E4DB'}
                    onChange={e => set('accentColor')(e.target.value)}
                    className="size-9 cursor-pointer rounded border border-border bg-transparent"
                    aria-label={t('branding.accentColor')}
                  />
                  <Input
                    value={form.accentColor}
                    onChange={e => set('accentColor')(e.target.value)}
                    placeholder="#F1E4DB"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="headingFont">{t('branding.headingFont')}</Label>
                <Input
                  id="headingFont"
                  value={form.headingFont}
                  onChange={e => set('headingFont')(e.target.value)}
                  placeholder="Playfair Display"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="bodyFont">{t('branding.bodyFont')}</Label>
                <Input
                  id="bodyFont"
                  value={form.bodyFont}
                  onChange={e => set('bodyFont')(e.target.value)}
                  placeholder="Inter"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('branding.misc')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="footerText">{t('branding.footerText')}</Label>
              <Input
                id="footerText"
                value={form.footerText}
                onChange={e => set('footerText')(e.target.value)}
                placeholder={t('branding.footerPlaceholder')}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="defaultLocale">{t('branding.defaultLocale')}</Label>
              <select
                id="defaultLocale"
                value={form.defaultLocale}
                onChange={e => set('defaultLocale')(e.target.value)}
                className="h-9 rounded-md border border-border bg-transparent px-3 text-sm"
              >
                <option value="fr">{t('common.french')}</option>
                <option value="en">{t('common.english')}</option>
              </select>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button type="submit" disabled={saving}>
            {saving && <Loader className="mr-2 size-4 animate-spin" />}
            {t('common.save')}
          </Button>
        </div>
      </fieldset>
    </form>
  )
}

export default BrandingSettings
